// Per-file table under the combinations — the same rows the CSV carries.
import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Search, X } from "lucide-react";
import { ACCOUNT_TONES, LEGAL_TONES } from "@/components/collections/JourneyLine";

const pill = "inline-flex items-center text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded border whitespace-nowrap";
const ACC_KEY = { Open: "open", Arrears: "arrears", Legal: "legal", Restructured: "restructured", Closed: "closed" };
const LEG_KEY = { "No legal action": "none", "Pre-action": "pre_action", "Ready for handover": "ready_for_handover", Live: "live", Paused: "paused", Done: "done" };
const STATUS_TONE = { fix: "text-rose-700", review: "text-amber-700", ok: "text-emerald-700" };
const MAX_ROWS = 400;

export function AuditFiles({ rows, total, selectedCombo, onClearCombo, onOpen, fmtGBP }) {
  const [q, setQ] = useState("");
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    const r = s ? rows.filter(x => (x.customer_name || "").toLowerCase().includes(s) || (x.loan_application_id || "").toLowerCase().includes(s) || (x.legal_owner || "").toLowerCase().includes(s)) : rows;
    return r.slice(0, MAX_ROWS);
  }, [rows, q]);
  return (
    <Card data-testid="audit-files">
      <CardHeader className="py-2.5 px-4 border-b flex flex-row items-center gap-3 flex-wrap">
        <CardTitle className="text-sm font-medium">{rows.length} file{rows.length === 1 ? "" : "s"}{rows.length !== total ? <span className="text-xs text-muted-foreground font-normal"> of {total}</span> : null}</CardTitle>
        {selectedCombo && (
          <button type="button" onClick={onClearCombo} className="inline-flex items-center gap-1 text-[11px] px-2 h-6 rounded border bg-indigo-50 border-indigo-200 text-indigo-900" data-testid="audit-files-combo-chip">
            {selectedCombo.ld} · {selectedCombo.account} · {selectedCombo.legal} <X className="w-3 h-3" />
          </button>
        )}
        <div className="relative ml-auto w-[260px]">
          <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Customer / SF / legal owner…" className="pl-7 h-8 text-xs" data-testid="audit-files-search" />
        </div>
      </CardHeader>
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Customer</th><th className="text-left px-2 py-2">LoanDisk</th><th className="text-left px-2 py-2">Journey</th>
              <th className="text-left px-2 py-2">Owners</th><th className="px-2 py-2">Diary</th><th className="px-2 py-2">Lists</th>
              <th className="text-left px-2 py-2">Stored</th><th className="text-right px-2 py-2">Balance</th><th className="text-left px-2 py-2 min-w-[220px]">Rule</th>
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-muted-foreground" data-testid="audit-files-empty">No files match.</td></tr>}
            {shown.map(r => (
              <tr key={r.case_id} onClick={() => onOpen(r)} className="border-t hover:bg-slate-50 cursor-pointer" data-testid={`audit-file-row-${r.case_id}`}>
                <td className="px-3 py-2">
                  <div className="font-medium text-slate-900 truncate max-w-[240px]">{r.customer_name || "—"}</div>
                  <div className="font-mono text-[10px] text-muted-foreground">{r.loan_application_id || ""}</div>
                </td>
                <td className="px-2 py-2 text-slate-700">{r.ld_status}</td>
                <td className="px-2 py-2">
                  <div className="flex items-center gap-1 flex-wrap">
                    <span className={`${pill} ${ACCOUNT_TONES[ACC_KEY[r.account]] || ACCOUNT_TONES.open}`}>{r.account}</span>
                    {LEG_KEY[r.legal] !== "none" && <span className={`${pill} ${LEGAL_TONES[LEG_KEY[r.legal]] || LEGAL_TONES.live}`}>⚖ {r.legal}</span>}
                  </div>
                  {r.legal_detail && <div className="text-[10px] text-slate-600 truncate max-w-[260px]">{r.legal_detail}</div>}
                </td>
                <td className="px-2 py-2 text-[11px]">
                  <div>{r.account_owner || <span className="text-slate-400 italic">no account owner</span>}</div>
                  <div className={r.legal_owner ? "text-red-800" : "text-slate-400 italic"}>{r.legal_owner ? `⚖ ${r.legal_owner}` : "no legal owner"}</div>
                </td>
                <td className="px-2 py-2 text-center tabular-nums">{r.open_actions || <span className="text-slate-300">0</span>}</td>
                <td className="px-2 py-2 text-center text-[10px] whitespace-nowrap">{[r.on_worklist && "W", r.on_tracker && "T"].filter(Boolean).join(" · ") || <span className="text-slate-300">—</span>}</td>
                <td className="px-2 py-2 text-[10px] text-slate-500">{[r.stage, r.legal_stage, r.route].filter(Boolean).join(" · ") || "—"}{r.hold ? " · hold" : ""}</td>
                <td className="px-2 py-2 text-right tabular-nums font-medium">{fmtGBP(r.balance_amount)}</td>
                <td className="px-2 py-2 leading-snug"><span className={`font-semibold uppercase text-[9px] mr-1 ${STATUS_TONE[r.status]}`}>{r.status}</span><span className="text-slate-600">{r.rule}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length > MAX_ROWS && <div className="px-4 py-2 text-[11px] text-muted-foreground">Showing the first {MAX_ROWS} — narrow the search or download the CSV for everything.</div>}
      </CardContent>
    </Card>
  );
}
